import { UTM_PARAM_KEYS } from '@percent/cause-dashboard/services/aichat/aichat'
import { GrantAdvisoryBookingUrlPayload } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

// The logical event-type key used to label analytics for this flow.
export const GRANT_ADVISORY_SOURCE = 'grant-advisory'

// Where the modal is surfaced from. Selects the per-entry-point booking URL and
// labels analytics events.
export type GrantAdvisoryEntryPoint = 'sidebar' | 'smartApply'

// UTM-style attribution seeded onto the booking URL, keyed by the entry point
// that surfaced the modal (sidebar promo vs smart apply).
export type BookingTracking = {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
}

// Analytics events for the booking CTA screen (questionnaire events live below).
export const GRANT_ADVISORY_BOOKING_EVENTS = {
  screenShown: 'Grant Advisory Booking Screen Shown',
  bookCallClicked: 'Grant Advisory Book Call Clicked',
  // The support-form link, offered to users who came for support rather than AgentPro.
  supportLinkClicked: 'Grant Advisory Support Link Clicked'
} as const

// Where the support-form link was clicked from.
export type SupportLinkPlacement = 'booking' | 'supportRedirect'

/*
  Resolve the booking base URL for an entry point: entry-point override → payload
  default. The PostHog payload is the single source of these URLs, so `undefined`
  here means booking is unavailable and the CTA must not be offered - we never fall
  back to a hardcoded link that could silently go stale or point at a retired host.

  A blank field falls through to `default` rather than counting as configured:
  clearing a per-entry-point value in the PostHog JSON editor is the natural way to
  say "use the shared link", and `??` alone would treat `''` as a real value.
*/
export const resolveBookingBaseUrl = (
  payload: GrantAdvisoryBookingUrlPayload | undefined,
  entryPoint: GrantAdvisoryEntryPoint
): string | undefined =>
  [payload?.[entryPoint], payload?.default].find(value => typeof value === 'string' && value.trim().length > 0)

/*
  Parse a payload-supplied booking URL, rejecting anything unusable.

  The payload is edited in PostHog, outside code review and without a deploy, so it
  is not trusted input: a link pasted without a scheme would throw from `new URL`,
  and a non-https scheme (`javascript:`, `data:`) would otherwise be handed straight
  to `window.open`. Both cases resolve to `undefined`, which callers already treat as
  "booking unavailable".
*/
const parseBookingUrl = (baseUrl: string | undefined): URL | undefined => {
  if (!baseUrl) {
    return undefined
  }

  try {
    const url = new URL(baseUrl)

    return url.protocol === 'https:' ? url : undefined
  } catch {
    return undefined
  }
}

/*
  Compose the final booking URL: seed UTM params from the entry-point `tracking`
  defaults, then overlay any utm_* present on the current page URL (page wins).

  `pageSearch` is passed in rather than read from `window` so callers control when
  the page's UTMs are sampled. Returns `undefined` for an unusable base URL - see
  `parseBookingUrl`.
*/
export const buildBookingUrl = (
  baseUrl: string | undefined,
  tracking?: BookingTracking,
  pageSearch: string = window.location.search
): string | undefined => {
  const url = parseBookingUrl(baseUrl)

  if (!url) {
    return undefined
  }

  const trackingUtm: Partial<Record<typeof UTM_PARAM_KEYS[number], string | undefined>> = {
    utm_source: tracking?.utmSource,
    utm_medium: tracking?.utmMedium,
    utm_campaign: tracking?.utmCampaign,
    utm_term: tracking?.utmTerm,
    utm_content: tracking?.utmContent
  }

  UTM_PARAM_KEYS.forEach(key => {
    const value = trackingUtm[key]
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  const pageParams = new URLSearchParams(pageSearch)
  UTM_PARAM_KEYS.forEach(key => {
    const value = pageParams.get(key)
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

// UTM attribution attached to the booking request, keyed by entry point
export const GRANT_ADVISORY_BOOKING_TRACKING: Record<GrantAdvisoryEntryPoint, BookingTracking> = {
  sidebar: {
    utmSource: 'sidebar',
    utmCampaign: 'grant_advisory',
    utmContent: 'sidebar_promo'
  },
  smartApply: {
    utmSource: 'popup',
    utmCampaign: 'grant_advisory',
    utmContent: 'smart_apply'
  }
}

export const SMART_APPLY_GRANT_ADVISORY_SOURCE = 'Smart Apply'
export const SMART_APPLY_GRANT_ADVISORY_EVENTS = {
  shown: 'Smart Apply Grant Advisory Shown',
  scheduled: 'Smart Apply Grant Advisory Meeting Scheduled'
} as const

// Asked from Smart Apply, behind the questionnaire experiment flags.
export type ExperimentQuestion = 'founded' | 'revenue' | 'grantExperience'

// The Yes/No eligibility check asked from the sidebar promo. `discountSupport` comes first
// and screens out support queries before the eligibility questions proper.
export type EligibilityQuestion = 'discountSupport' | 'registeredCharity' | 'overOneYear'

export type QuestionnaireQuestion = ExperimentQuestion | EligibilityQuestion

// Questions whose disqualifying answers are recorded but not acted on in the
// non-blocking variant. `founded` is excluded on purpose: under a year old is the
// one answer the service cannot work with, so it still routes to Sorry.
export const NON_BLOCKING_QUESTIONS: readonly QuestionnaireQuestion[] = ['revenue', 'grantExperience']

export type QuestionnaireNonBlockingVariant = 'control' | 'non_blocking'

// Only the exact string turns blocking off. `undefined` (flags loading, PostHog
// unconfigured) or a typo in the PostHog variant name keeps the gate on.
export const resolveNonBlockingVariant = (variant: string | boolean | undefined): QuestionnaireNonBlockingVariant =>
  variant === 'non_blocking' ? 'non_blocking' : 'control'

export const GRANT_ADVISORY_QUESTIONNAIRE_EVENTS = {
  started: 'Grant Advisory Questionnaire Started',
  qualified: 'Grant Advisory Questionnaire Qualified',
  sorryShown: 'Grant Advisory Questionnaire Sorry Shown',
  supportRedirectShown: 'Grant Advisory Questionnaire Support Redirect Shown',
  answered: {
    founded: 'Grant Advisory Questionnaire Founded Answered',
    revenue: 'Grant Advisory Questionnaire Revenue Answered',
    grantExperience: 'Grant Advisory Questionnaire Grant Experience Answered',
    discountSupport: 'Grant Advisory Questionnaire Discount Support Answered',
    registeredCharity: 'Grant Advisory Questionnaire Registered Charity Answered',
    overOneYear: 'Grant Advisory Questionnaire Over One Year Answered'
  } as Record<QuestionnaireQuestion, string>
} as const
